import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Ref } from "vue";
import { request } from "@/shared/http/client";
import type { AgentMode, AgentSession, ModelTier, SessionSubject } from "./use-agents";

/** O que o servidor entendeu do pedido. Nunca escrito por esta camada. */
export type AgentDoor = "panel" | "conversation";

/** O agente como uma doca precisa dele: quem é, que cara tem, se está ligado. */
export interface BenView {
	readonly code: string;
	readonly name: string;
	readonly mode: AgentMode;
	readonly tier: ModelTier;
	readonly avatarUrl: string | null;
	readonly isActive: boolean;
}

/** Envelope NOMEADO, como o servidor o escreve — nunca `{ items }`. */
export interface BenAnswer {
	/** Null when no internal agent is available. */
	readonly ben: BenView | null;
	/** A thread daquele assunto, LIDA e nunca criada. `null` até a primeira pergunta. */
	readonly session: AgentSession | null;
	readonly door: AgentDoor;
	/** A lista do seletor da doca, na ordem em que ela a oferece. */
	readonly reachable: readonly BenView[];
}

/** A chave de cache desta pergunta — exportada porque quem arquiva uma thread
 *  precisa de a invalidar, e um literal repetido é a metade que fica para trás. */
export const BEN_QUERY_KEY = "ben";

/** The server resolves the default, explicit historical agent and conversation subject. */
export function useBen(input: {
	subject: Ref<SessionSubject | null>;
	preferred: Ref<string | null>;
}) {
	return useQuery({
		queryKey: [BEN_QUERY_KEY, input.subject, input.preferred],
		queryFn: async (): Promise<BenAnswer> => {
			const params = new URLSearchParams();
			const about = input.subject.value;
			if (about) params.set("subject", `${about.kind}:${about.id}`);
			const preferred = input.preferred.value;
			if (preferred) params.set("agent", preferred);
			const query = params.toString();
			return request<BenAnswer>(`/v3/agents/ben${query ? `?${query}` : ""}`);
		},
	});
}

/**
 * A resposta desta porta ficou velha: quem pergunta de novo é a doca.
 *
 * Chamado depois de abrir ou de arquivar uma thread com assunto — as duas coisas
 * que mudam o `session` que veio nesta resposta. Sem isto, reabrir a coluna
 * mostraria a thread que já não existe, ou não mostraria a que acabou de nascer.
 */
export function useForgetBen() {
	const client = useQueryClient();
	return () => client.invalidateQueries({ queryKey: [BEN_QUERY_KEY] });
}
